import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EnvironmentOutlined, CalendarOutlined, CarOutlined } from '@ant-design/icons';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { ROUTES, START_DATE_BUFFER_DAYS } from '@utils/constants';
import { formatDurationRange, calculateEstimatedPickupTime } from '@utils/formatters';
import { getMinStartDate } from '@utils/dateFormatter';
import { RoutingDisclaimer } from '@components/ui/RoutingDisclaimer/RoutingDisclaimer';
import { AddressSelect } from '../../components/ui/AddressSelect';
import { updateStudentSession, getStudentSession } from '@utils/sessionStorage';
import { AppHeader } from '@components/layout/AppHeader';
import { InfoCard } from '@components/ui/InfoCard';
import { ModalAlert, type ModalAlertType } from '@components/ui/Alert/ModalAlert';
import { getDistanceMatrix, type DistanceMatrixResult } from '@api/maps';
import { listPricingConfigs, type PricingConfig } from '@api/pricing';
import './styles/pickuplocation.css';
import '@styles/loader.css';

interface LocationCoords {
  lat: number;
  lng: number;
}

interface PickupLocationState {
  formData: any;
  campuses: any[];
  shifts: any[];
}

interface TransportOption {
  type: 'ac' | 'non-ac';
  label: string;
  icon: string;
  description: string;
  plan: PricingConfig;
}

export const PickupLocation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as PickupLocationState;

  const [selectedLocation, setSelectedLocation] = useState<LocationCoords | null>(null);
  const [address, setAddress] = useState('');
  const [startDate, setStartDate] = useState('');
  const [transportType, setTransportType] = useState<'ac' | 'non-ac' | ''>('');
  const [formData, setFormData] = useState<any>(null);
  const [campuses, setCampuses] = useState<any[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [distanceInfo, setDistanceInfo] = useState<DistanceMatrixResult | null>(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [transportOptions, setTransportOptions] = useState<TransportOption[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [estimatedPickupTime, setEstimatedPickupTime] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ location?: string; startDate?: string; transportType?: string }>({});

  // Refs for scroll-to-error
  const locationRef = React.useRef<HTMLDivElement>(null);
  const startDateRef = React.useRef<HTMLDivElement>(null);
  const transportTypeRef = React.useRef<HTMLDivElement>(null);

  const [modalAlertConfig, setModalAlertConfig] = useState<{
    type: ModalAlertType;
    title: string;
    description?: string;
    visible: boolean;
  }>({
    type: 'info',
    title: '',
    description: '',
    visible: false,
  });

  const getMinDate = () => {
    return getMinStartDate(START_DATE_BUFFER_DAYS);
  };

  const getShiftDateRange = () => {
    if (formData?.shift?.validFrom && formData?.shift?.validUntil) {
      return {
        validFrom: formData.shift.validFrom,
        validUntil: formData.shift.validUntil,
      };
    }
    return null;
  };

  // Check if start date is within valid range
  const isStartDateValid = () => {
    if (!startDate) return false;
    
    // Date must be at least START_DATE_BUFFER_DAYS from today (IST)
    const selectedDate = dayjs(startDate);
    const minAllowedDate = dayjs(getMinStartDate(START_DATE_BUFFER_DAYS));
    if (selectedDate.isBefore(minAllowedDate)) return false;
    
    const shiftRange = getShiftDateRange();
    if (!shiftRange) return true; // If no shift range, just needs to meet buffer requirement
    
    const shiftValidFrom = dayjs(shiftRange.validFrom);
    const shiftValidUntil = dayjs(shiftRange.validUntil);
    
    return (selectedDate.isSame(shiftValidFrom) || selectedDate.isAfter(shiftValidFrom)) && 
           (selectedDate.isSame(shiftValidUntil) || selectedDate.isBefore(shiftValidUntil));
  };

  // Get shift date range display text
  const getShiftDateRangeText = () => {
    const shiftRange = getShiftDateRange();
    if (!shiftRange) return null;
    
    const validFrom = dayjs(shiftRange.validFrom).format('DD/MM/YYYY');
    const validUntil = dayjs(shiftRange.validUntil).format('DD/MM/YYYY');
    return `${validFrom} - ${validUntil}`;
  };
  // Restore session data on mount
  useEffect(() => { 
    const session = getStudentSession();
    
    // Restore from session storage
    if (session?.pickupLocation) {
      setSelectedLocation(session.pickupLocation.coordinates);
      setAddress(session.pickupLocation.address);
      
      // Calculate distance if campus location is available
      const campusLocation = session?.formData?.campus?.location;
      if (campusLocation && campusLocation.lat && campusLocation.lng) {
        // Call calculateDistance with campus location to ensure it works
        calculateDistanceFromCoords(
          session.pickupLocation.coordinates,
          { lat: campusLocation.lat, lng: campusLocation.lng }
        );
      }
    }
    if (session?.startDate) {
      setStartDate(session.startDate);
    }
    if (session?.transportType) {
      const type = session.transportType as 'ac' | 'non-ac';
      setTransportType(type);
    }
    
    // Restore form data from state or session
    if (state?.formData) {
      setFormData(state.formData);
      setCampuses(state.campuses || []);
      setShifts(state.shifts || []);
    } else if (session?.formData) {
      // If coming back from another page, restore from session
      setFormData({
        givenName: session.formData.givenName,
        familyName: session.formData.familyName,
        gender: session.formData.gender,
        grade: session.formData.grade,
        campus: session.formData.campus,
        shift: session.formData.shift,
      });
    }
  }, [state]);

  // Fetch pricing plans once when component mounts
  useEffect(() => {
    if (formData?.shift?.id) {
      fetchPricingPlans();
    } else {
      setLoadingPlans(false);
    }
  }, [formData?.shift?.id]);

  const fetchPricingPlans = async () => {
    setLoadingPlans(true);
    try {
      const pricingConfigs = await listPricingConfigs(formData.shift.id);

      // Filter plans by vehicle type (AC and NON_AC)
      const acPlans = pricingConfigs.filter(plan => plan.vehicleType === 'AC');
      const nonAcPlans = pricingConfigs.filter(plan => plan.vehicleType === 'NON_AC');

      // Build transport options based on available plans
      const options: TransportOption[] = [];

      if (acPlans.length > 0) {
        options.push({
          type: 'ac',
          label: 'AC Transport',
          icon: '❄️',
          description: 'Climate controlled comfort',
          plan: acPlans[0],
        });
      }

      if (nonAcPlans.length > 0) {
        options.push({
          type: 'non-ac',
          label: 'Non-AC Transport',
          icon: '🌬️',
          description: 'Well-ventilated & economical',
          plan: nonAcPlans[0],
        });
      }

      setTransportOptions(options);

      // Select first option by default
      if (options.length > 0) {
        setTransportType(options[0].type);
      }
    } catch (err) {
      console.error('Error fetching pricing plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  };

  if (!formData && !state?.formData) {
    navigate(ROUTES.STUDENT_DETAILS);
    return null;
  }

  const handleContinue = () => {
    const newErrors: { location?: string; startDate?: string; transportType?: string } = {};

    if (!selectedLocation || !address) {
      newErrors.location = 'Please select a pickup location from the list';
    }
    if (!startDate) {
      newErrors.startDate = 'Please select a service start date';
    } else if (!isStartDateValid()) {
      const shiftRangeText = getShiftDateRangeText();
      newErrors.startDate = shiftRangeText
        ? `Please select a date within the valid range: ${shiftRangeText}`
        : `Please select a valid date at least ${START_DATE_BUFFER_DAYS} days from today`;
    }
    if (!transportType || (transportType !== 'ac' && transportType !== 'non-ac')) {
      newErrors.transportType = 'Please select a transport type';
    }

    setValidationErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Scroll to first error in order
      if (newErrors.location) {
        locationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (newErrors.startDate) {
        startDateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (newErrors.transportType) {
        transportTypeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Check if distance is within serviceable range (45 km)
    if (distanceInfo && distanceInfo.distanceKm > 45) {
      setModalAlertConfig({
        type: 'error',
        title: 'Distance not serviceable',
        description: 'Currently we are unable to serve beyond 45km please try again later.',
        visible: true,
      });
      return;
    }

    // Update session with pickup location, start date, and transport type
    updateStudentSession({
      pickupLocation: {
        coordinates: selectedLocation!,
        address,
      },
      startDate,
      transportType,
      pricingConfig: transportOptions.find(opt => opt.type === transportType)?.plan,
    });

    navigate(ROUTES.REVIEW_DETAILS, {
      state: {
        formData: formData || state?.formData,
        campuses: campuses || state?.campuses,
        shifts: shifts || state?.shifts,
        pickupLocation: {
          coordinates: selectedLocation,
          address,
        },
        startDate,
        transportType,
        pricingConfig: transportOptions.find(opt => opt.type === transportType)?.plan,
        distance: distanceInfo?.distanceKm,
        distanceText: distanceInfo?.distanceText,
        duration: distanceInfo?.durationMinutes,
        durationText: distanceInfo?.durationText,
        estimatedPickupTime: estimatedPickupTime,
      },
    });
  };

  const handleLocationSelect = (location: LocationCoords) => {
    setSelectedLocation(location);
    if (validationErrors.location) {
      setValidationErrors(prev => ({ ...prev, location: undefined }));
    }
    calculateDistance(location);
  };

  const calculateDistance = async (location: LocationCoords) => {
    try {
      setCalculatingDistance(true);
      
      // Get campus location from formData
      const campusLocation = formData?.campus?.location;
      
      if (!campusLocation || !campusLocation.lat || !campusLocation.lng) {
        console.warn('Campus location not available');
        setCalculatingDistance(false);
        return;
      }

      // Calculate distance using the backend API
      const result = await getDistanceMatrix(
        location.lat,
        location.lng,
        campusLocation.lat,
        campusLocation.lng
      );

      setDistanceInfo(result);

      // Store distance info in session storage
      updateStudentSession({
        distanceInfo: result,
      });
    } catch (err: any) {
      console.error('Error calculating distance:', err);
      setDistanceInfo(null);
    } finally {
      setCalculatingDistance(false);
    }
  };

  // Helper function to calculate distance with explicit campus location (for use on page load)
  const calculateDistanceFromCoords = async (
    pickupLocation: LocationCoords,
    campusLocation: { lat: number; lng: number }
  ) => {
    try {
      setCalculatingDistance(true);

      // Calculate distance using the backend API
      const result = await getDistanceMatrix(
        pickupLocation.lat,
        pickupLocation.lng,
        campusLocation.lat,
        campusLocation.lng
      );

      setDistanceInfo(result);

      // Store distance info in session storage
      updateStudentSession({
        distanceInfo: result,
      });
    } catch (err: any) {
      console.error('Error calculating distance:', err);
      setDistanceInfo(null);
    } finally {
      setCalculatingDistance(false);
    }
  };

  // Calculate estimated pickup time when distance info and shift start time are available
  useEffect(() => {
    if (distanceInfo?.durationMinutes && formData?.shift?.schedule?.startsAt) {
      const pickupTime = calculateEstimatedPickupTime(
        formData.shift.schedule?.startsAt,
        distanceInfo.durationMinutes
      );
      setEstimatedPickupTime(pickupTime);
    } else {
      setEstimatedPickupTime(null);
    }
  }, [distanceInfo?.durationMinutes, formData?.shift?.schedule?.startsAt]);

  return (
    <div className="pickup-location-page">
      {/* Header */}
      <AppHeader />

      {/* Main Content */}
      <div className="pickup-location-page__container">
        <div className="pickup-location-page__content">
          {/* Modal Alert */}
          <ModalAlert
            type={modalAlertConfig.type}
            title={modalAlertConfig.title}
            description={modalAlertConfig.description}
            visible={modalAlertConfig.visible}
            onConfirm={() => setModalAlertConfig({ ...modalAlertConfig, visible: false })}
            confirmText="OK"
          />
          {/* Title Section */}
          <div className="pickup-location-page__title-section">
            <h1 className="pickup-location-page__title">Setup your service</h1>
            <p className="pickup-location-page__subtitle">Configure pickup location, start date, and transport type</p>
          </div>

          {/* Pickup Location Section */}
          <div className={`pickup-location-page__section ${validationErrors.location ? 'pickup-location-page__section--error' : ''}`} ref={locationRef}>
            <div className="pickup-location-page__section-header">
              <EnvironmentOutlined className="pickup-location-page__section-icon" />
              <div>
                <h3 className="pickup-location-page__section-title">Pickup location</h3>
                <p className="pickup-location-page__section-subtitle">AC doorstep pickup & drop</p>
              </div>
            </div>

            {/* Address Select Component */}
            <AddressSelect
              value={address}
              onAddressChange={(val) => {
                setAddress(val);
                if (validationErrors.location) {
                  setValidationErrors(prev => ({ ...prev, location: undefined }));
                }
              }}
              onLocationSelect={handleLocationSelect}
              onLocationClear={() => {
                setSelectedLocation(null);
                setDistanceInfo(null);
                setEstimatedPickupTime(null);
              }}
              initialLocation={selectedLocation || undefined}
              placeholder="Search for your address..."
              error={validationErrors.location}
            />

            {/* Distance Info */}
            {calculatingDistance && (
              <div className="pickup-location-page__distance-card">
                <span className="btn-loader btn-loader--small"></span>
                <span>Calculating distance...</span>
              </div>
            )}
            {distanceInfo && !calculatingDistance && (
              <div className="pickup-location-page__distance-card">
                <div className="pickup-location-page__distance-row">
                  <span>📍 Distance to campus:</span>
                  <strong>{distanceInfo.distanceText}</strong>
                </div>
                {estimatedPickupTime && (
                  <div className="pickup-location-page__distance-row">
                    <span>⏰ Estimated pickup time:</span>
                    <strong>{estimatedPickupTime}</strong>
                  </div>
                )}
                {distanceInfo?.durationMinutes && (
                  <div className="pickup-location-page__distance-row">
                    <span>⏰ Travel time:</span>
                    <strong>{formatDurationRange(distanceInfo.durationMinutes)}</strong>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Service Start Date Section */}
          <div className={`pickup-location-page__section ${validationErrors.startDate ? 'pickup-location-page__section--error' : ''}`} ref={startDateRef}>
            <div className="pickup-location-page__section-header">
              <CalendarOutlined className="pickup-location-page__section-icon" />
              <div>
                <h3 className="pickup-location-page__section-title">Service start date</h3>
                <p className="pickup-location-page__section-subtitle">When should we begin your service?</p>
              </div>
            </div>

            <div className="pickup-location-page__form-group">
              <DatePicker
                value={startDate ? dayjs(startDate) : null}
                defaultPickerValue={(() => {
                  const minDate = dayjs(getMinDate());
                  const shiftRange = getShiftDateRange();
                  if (shiftRange) {
                    const shiftValidFrom = dayjs(shiftRange.validFrom);
                    return shiftValidFrom.isAfter(minDate) ? shiftValidFrom : minDate;
                  }
                  return minDate;
                })()}
                onChange={(date) => {
                  if (date) {
                    setStartDate(date.format('YYYY-MM-DD'));
                    if (validationErrors.startDate) {
                      setValidationErrors(prev => ({ ...prev, startDate: undefined }));
                    }
                  }
                }}
                disabledDate={(current) => {
                  const minDate = dayjs(getMinDate());
                  const shiftRange = getShiftDateRange();

                  // Disable dates before the minimum allowed date
                  if (current && current < minDate) {
                    return true;
                  }

                  // If shift has date range, disable dates outside that range
                  if (shiftRange) {
                    const shiftValidFrom = dayjs(shiftRange.validFrom);
                    const shiftValidUntil = dayjs(shiftRange.validUntil);

                    // Disable if before shift start or after shift end
                    if (current && (current < shiftValidFrom || current > shiftValidUntil)) {
                      return true;
                    }
                  }

                  return false;
                }}
                format="DD/MM/YYYY"
                placeholder="Select date"
                inputReadOnly
                className="pickup-location-page__ant-date-picker"
              />
              {/* Error message for invalid date range */}
              {validationErrors.startDate && (
                <p className="pickup-location-page__error">{validationErrors.startDate}</p>
              )}
              {!validationErrors.startDate && startDate && !isStartDateValid() && getShiftDateRangeText() && (
                <p className="pickup-location-page__error">
                  Please select a date within the valid range: {getShiftDateRangeText()}
                </p>
              )}
              <RoutingDisclaimer variant="full" />
            </div>
          </div>

          {/* Transport Type Section */}
          <div className={`pickup-location-page__section ${validationErrors.transportType ? 'pickup-location-page__section--error' : ''}`} ref={transportTypeRef}>
            <div className="pickup-location-page__section-header">
              <CarOutlined className="pickup-location-page__section-icon" />
              <div>
                <h3 className="pickup-location-page__section-title">Transport type</h3>
                <p className="pickup-location-page__section-subtitle">Select your preferred option(s)</p>
              </div>
            </div>

            {!loadingPlans && transportOptions.length === 0 ? (
              <div className="pickup-location-page__no-plans">
                <p>No pricing configurations available for this selection.</p>
                <p>Please contact support for assistance.</p>
              </div>
            ) : (
              <div className="pickup-location-page__transport-options">
                {transportOptions.map((option) => (
                  <button
                    key={option.type}
                    type="button"
                    className={`pickup-location-page__transport-option ${transportType === option.type ? 'active' : ''}`}
                    onClick={() => {
                      setTransportType(option.type);
                      if (validationErrors.transportType) {
                        setValidationErrors(prev => ({ ...prev, transportType: undefined }));
                      }
                    }}
                  >
                    <span className={`pickup-location-page__transport-icon ${option.type === 'non-ac' ? 'pickup-location-page__transport-icon--green' : ''}`}>
                      {option.icon}
                    </span>
                    <div className="pickup-location-page__transport-text">
                      <p className="pickup-location-page__transport-title">{option.label}</p>
                      <p className="pickup-location-page__transport-desc">{option.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {validationErrors.transportType && (
              <p className="pickup-location-page__error">{validationErrors.transportType}</p>
            )}
          </div>

          {/* Info Card */}
          <InfoCard
            icon={<span style={{ fontSize: '24px' }}>💡</span>}
            title="DID YOU KNOW?"
            description="Omni's seating capacity is 5 including the driver. Anything more is illegal and unsafe."
            className="pickup-location-info-card"
          />

          {/* Footer Buttons */}
          <div className="pickup-location-page__footer">
            <button
              onClick={handleContinue}
              className="pickup-location-page__button"
            >
              Continue →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
